import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const copyByLang = {
  es: {
    title: "Esports en Puerto Rico | PREL",
    description: "Una mirada clara al ecosistema de esports en Puerto Rico: competencia, comunidad, contenido, cultura competitiva y espacio para crecimiento.",
    keywords: "esports en Puerto Rico, gaming Puerto Rico, ecosistema esports Puerto Rico, cultura gaming Puerto Rico",
    ogDescription: "Una explicación clara sobre cómo se mueve el ecosistema de esports en Puerto Rico y por qué todavía hay espacio para crecer.",
    back: "← Volver al inicio",
    eyebrow: "Contexto",
    heroTitle: "Esports en Puerto Rico",
    heroBody:
      "El ecosistema de esports en Puerto Rico ha crecido mezclando competencia, comunidad, cultura gaming, contenido y entretenimiento. Aunque todavía hay espacio para profesionalizar más la industria, ya existe una base real de audiencias, eventos, talento y plataformas que están empujando el sector hacia adelante.",
    sections: [
      {
        title: "Un ecosistema más amplio que solo torneos",
        body: "Cuando se habla de esports en Puerto Rico, muchas veces la conversación se queda en competencias puntuales. Pero el ecosistema también incluye contenido, comunidades, producción audiovisual, espacios presenciales, marcas, instituciones y medios que ayudan a darle continuidad al movimiento.",
      },
      {
        title: "Gaming, cultura y entretenimiento se mezclan",
        body: "En Puerto Rico, los esports no viven aislados. Se mezclan con cultura popular, música, entretenimiento, educación, televisión y experiencias presenciales. Esa intersección hace que el sector tenga un lenguaje distinto y oportunidades distintas a las de otros mercados.",
      },
      {
        title: "Todavía hay espacio para construir",
        body: "El ecosistema local sigue creciendo y todavía hay mucho por profesionalizar: calendarios, visibilidad, infraestructura, desarrollo de talento y continuidad entre eventos, contenido y alianzas. Eso también significa que hay oportunidad real para quienes quieran construir a largo plazo.",
      },
    ],
    prelTitle: "Dónde entra PREL en esta conversación",
    prelBody:
      "Puerto Rico Esports League forma parte de ese crecimiento al conectar eventos, medios, comunidad y proyectos de marca dentro de una misma plataforma. Su aporte no está solo en la competencia, sino en ayudar a que el gaming local tenga narrativa, continuidad y presencia cultural.",
  },
  en: {
    title: "Esports in Puerto Rico | PREL",
    description: "A clear look at Puerto Rico's esports ecosystem: competition, community, content, competitive culture, and room for growth.",
    keywords: "esports in Puerto Rico, Puerto Rico gaming, Puerto Rico esports ecosystem, Puerto Rico gaming culture",
    ogDescription: "A clear explanation of how the esports ecosystem moves in Puerto Rico and why there is still room to grow.",
    back: "← Back to home",
    eyebrow: "Context",
    heroTitle: "Esports in Puerto Rico",
    heroBody:
      "Puerto Rico's esports ecosystem has grown by blending competition, community, gaming culture, content, and entertainment. While there is still room to professionalize the industry further, there is already a real base of audiences, events, talent, and platforms pushing the sector forward.",
    sections: [
      {
        title: "A broader ecosystem than just tournaments",
        body: "When people talk about esports in Puerto Rico, the conversation often stays focused on isolated competitions. But the ecosystem also includes content, communities, audiovisual production, in-person spaces, brands, institutions, and media that help give the movement continuity.",
      },
      {
        title: "Gaming, culture, and entertainment overlap",
        body: "In Puerto Rico, esports do not live in isolation. They blend with pop culture, music, entertainment, education, television, and live experiences. That intersection gives the sector a different language and different opportunities than other markets.",
      },
      {
        title: "There is still room to build",
        body: "The local ecosystem is still growing and there is still a lot to professionalize: calendars, visibility, infrastructure, talent development, and continuity between events, content, and partnerships. That also means there is real opportunity for people who want to build for the long term.",
      },
    ],
    prelTitle: "Where PREL fits into this conversation",
    prelBody:
      "Puerto Rico Esports League is part of that growth by connecting events, media, community, and brand projects within a single platform. Its contribution is not only in competition, but in helping local gaming build narrative, continuity, and cultural presence.",
  },
};

export default function EsportsInPuertoRicoPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("keywords", copy.keywords);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "article", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/esports-en-puerto-rico", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/esports-en-puerto-rico");

    const schemaId = "prel-esports-pr-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: copy.heroTitle,
      description: copy.description,
      author: {
        "@type": "Organization",
        name: "Puerto Rico Esports League",
      },
      publisher: {
        "@type": "Organization",
        name: "Puerto Rico Esports League",
        logo: {
          "@type": "ImageObject",
          url: "https://www.puertoricoesportleague.com/logo.png",
        },
      },
      mainEntityOfPage: "https://www.puertoricoesportleague.com/esports-en-puerto-rico",
    });
  }, [copy]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="" />

      <main className="max-w-[1200px] mx-auto px-6 xl:px-8 pt-12 pb-20">
        <a href="/" className="text-cyan-300 text-sm hover:underline">{copy.back}</a>
        <p className="mt-8 text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.eyebrow}</p>
        <h1 className="mt-4 text-[clamp(2.7rem,7vw,5.5rem)] font-black leading-[0.92] uppercase">{copy.heroTitle}</h1>
        <p className="mt-6 max-w-4xl text-slate-300 text-lg leading-relaxed">{copy.heroBody}</p>

        <div className="mt-10 grid gap-6">
          {copy.sections.map((section) => (
            <article key={section.title} className="border border-white/10 bg-[#111722] p-7 md:p-8">
              <h2 className="text-3xl font-black uppercase">{section.title}</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">{section.body}</p>
            </article>
          ))}
        </div>

        <section className="mt-10 border border-white/10 bg-[#111722] p-7 md:p-8">
          <h2 className="text-3xl font-black uppercase">{copy.prelTitle}</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">{copy.prelBody}</p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
